import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ListGroup, Modal, Text } from '@quality24/design-system';

import {
  LanguageOption,
  SUPPORTED_LANGUAGES,
} from '@/inpatient-patient-pwa/i18n/constants';

export interface Props {
  show: boolean;
  onHide: () => void;
  /**
   * Languages to offer — normally the hospital's set, resolved by
   * `LanguageProvider`. Defaults to every language the app ships.
   */
  options?: ReadonlyArray<LanguageOption>;
}

/**
 * Modal that lets the user pick the app language. The selection is only
 * applied (and persisted) when the user confirms via "Save"; closing with the
 * X discards the pending choice.
 */
const LanguagePickerModal: React.FunctionComponent<Props> = ({
  show,
  onHide,
  options = SUPPORTED_LANGUAGES,
}) => {
  const { t, i18n } = useTranslation();
  const [pendingLanguage, setPendingLanguage] = React.useState(i18n.language);

  // Reset the pending selection to the active language each time it opens.
  React.useEffect(() => {
    if (show) setPendingLanguage(i18n.language);
  }, [show, i18n.language]);

  const handleSave = React.useCallback(() => {
    if (pendingLanguage !== i18n.language) {
      // changeLanguage persists to localStorage (detector cache) and triggers
      // the moment/Apollo side effects registered elsewhere.
      i18n.changeLanguage(pendingLanguage);
    }
    onHide();
  }, [i18n, pendingLanguage, onHide]);

  return (
    <Modal
      id="language-picker-modal"
      show={show}
      title={t('language.modal.title')}
      onHide={onHide}
      footer={
        <Button onClick={handleSave} size="lg" fluid>
          {t('language.modal.save')}
        </Button>
      }
    >
      <Text className="mb-3">{t('language.modal.subtitle')}</Text>
      <ListGroup
        value={pendingLanguage}
        onChange={(value) => {
          // Ignore the toggle-off (undefined) so a language is always selected.
          if (value) setPendingLanguage(value);
        }}
      >
        {options.map((language) => (
          <ListGroup.Item key={language.value} value={language.value}>
            {language.label}
          </ListGroup.Item>
        ))}
      </ListGroup>
    </Modal>
  );
};

export default LanguagePickerModal;
